import { useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleMap,
  MarkerF,
  useGoogleMap,
} from '@react-google-maps/api';
import { useGoogleMaps } from './GoogleMapsContext';

const toFloat = (value) => parseFloat(parseFloat(value).toFixed(8));

const getMarkerLabel = (index) => {
  let value = index + 1;
  let label = '';

  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }

  return label;
};

const getPointKey = (point) =>
  `${toFloat(point.latitude)}:${toFloat(point.longitude)}`;

const hasValidCoordinates = (point) => {
  const latitude = toFloat(point.latitude);
  const longitude = toFloat(point.longitude);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

function NativeDirectionsRenderer({ directions }) {
  const map = useGoogleMap();

  useEffect(() => {
    if (!map || !directions) return;

    const renderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
    });
    renderer.setDirections(directions);
    renderer.setMap(map);

    return () => {
      renderer.setMap(null);
    };
  }, [directions, map]);

  return null;
}

function NPMapViewDirections({
  origins = [],
  dropoffs = [],
  onWaypointsOptimized,
  onDistanceCalculated,
}) {
  const { isLoaded, loadError } = useGoogleMaps();

  const [directions, setDirections] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 49.8951, lng: -97.1384 });
  const [shouldCenter, setShouldCenter] = useState(true);

  const directionsServiceRef = useRef(null);
  const prevMapDataRef = useRef({ origins: [], dropoffs: [] });
  const routeRequestIdRef = useRef(0);

  const mapOrigins = useMemo(
    () =>
      origins
        .map((origin) => ({
          latitude: origin.latitude,
          longitude: origin.longitude,
          address: origin.address,
        }))
        .filter(hasValidCoordinates),
    [origins]
  );

  const mapDropoffs = useMemo(
    () =>
      dropoffs
        .map((dropoff) => ({
          latitude: dropoff.latitude,
          longitude: dropoff.longitude,
          address: dropoff.address,
        }))
        .filter(hasValidCoordinates),
    [dropoffs]
  );

  const routePoints = useMemo(
    () => [...mapOrigins, ...mapDropoffs],
    [mapOrigins, mapDropoffs]
  );

  const markerPoints = useMemo(() => {
    const seen = new Set();

    return routePoints.filter((point) => {
      const key = getPointKey(point);

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }, [routePoints]);

  useEffect(() => {
    if (!isLoaded || directionsServiceRef.current) return;

    directionsServiceRef.current = new window.google.maps.DirectionsService();
  }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    if (mapOrigins.length === 0 || mapDropoffs.length === 0) {
      routeRequestIdRef.current += 1;
      prevMapDataRef.current = { origins: mapOrigins, dropoffs: mapDropoffs };
      setDirections(null);
      return;
    }

    const prevMapData = prevMapDataRef.current;
    const originsChanged =
      prevMapData.origins.length !== mapOrigins.length ||
      prevMapData.origins.some(
        (origin, index) =>
          origin.latitude !== mapOrigins[index]?.latitude ||
          origin.longitude !== mapOrigins[index]?.longitude ||
          origin.address !== mapOrigins[index]?.address
      );
    const dropoffsChanged =
      prevMapData.dropoffs.length !== mapDropoffs.length ||
      prevMapData.dropoffs.some(
        (dropoff, index) =>
          dropoff.latitude !== mapDropoffs[index]?.latitude ||
          dropoff.longitude !== mapDropoffs[index]?.longitude ||
          dropoff.address !== mapDropoffs[index]?.address
      );

    if (!originsChanged && !dropoffsChanged) return;

    const requestId = routeRequestIdRef.current + 1;
    routeRequestIdRef.current = requestId;
    prevMapDataRef.current = { origins: mapOrigins, dropoffs: mapDropoffs };
    setDirections(null);

    const directionsService = directionsServiceRef.current;
    if (!directionsService) return;

    const originPoint = routePoints[0];
    const destinationPoint = routePoints[routePoints.length - 1];

    if (!originPoint || !destinationPoint) return;

    const waypointPoints = routePoints.slice(1, -1);
    const waypoints = waypointPoints.map((point) => ({
      location: new window.google.maps.LatLng(
        toFloat(point.latitude),
        toFloat(point.longitude)
      ),
      stopover: true,
    }));

    const request = {
      origin: new window.google.maps.LatLng(
        toFloat(originPoint.latitude),
        toFloat(originPoint.longitude)
      ),
      destination: new window.google.maps.LatLng(
        toFloat(destinationPoint.latitude),
        toFloat(destinationPoint.longitude)
      ),
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };

    directionsService.route(request, (result, status) => {
      if (requestId !== routeRequestIdRef.current) return;

      if (status !== window.google.maps.DirectionsStatus.OK) {
        console.error('Error fetching directions:', status);
        return;
      }

      setDirections(result);

      if (onWaypointsOptimized) {
        const waypointOrder = result.routes[0].waypoint_order || [];
        const orderedWaypointPoints =
          waypointOrder.length > 0
            ? waypointOrder.map((index) => waypointPoints[index])
            : waypointPoints;
        const orderedRoutePoints = [
          originPoint,
          ...orderedWaypointPoints,
          destinationPoint,
        ];

        onWaypointsOptimized(
          orderedRoutePoints.map((point) => ({
            location: new window.google.maps.LatLng(
              toFloat(point.latitude),
              toFloat(point.longitude)
            ),
          }))
        );
      }

      if (onDistanceCalculated) {
        const totalDistance = result.routes[0].legs.reduce(
          (total, leg) => total + leg.distance.value,
          0
        );
        onDistanceCalculated(totalDistance / 1000);
      }

      if (shouldCenter) {
        setMapCenter({
          lat: toFloat(originPoint.latitude),
          lng: toFloat(originPoint.longitude),
        });
        setShouldCenter(false);
      }
    });
  }, [
    isLoaded,
    mapOrigins,
    mapDropoffs,
    routePoints,
    shouldCenter,
    onWaypointsOptimized,
    onDistanceCalculated,
  ]);

  useEffect(() => {
    setShouldCenter(true);
  }, [mapOrigins, mapDropoffs]);

  useEffect(() => {
    if (markerPoints.length !== 1) return;

    const point = markerPoints[0];
    setMapCenter({
      lat: toFloat(point.latitude),
      lng: toFloat(point.longitude),
    });
    setShouldCenter(false);
  }, [markerPoints]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '400px' }}
      center={mapCenter}
      zoom={13}
    >
      <NativeDirectionsRenderer directions={directions} />
      {markerPoints.map((point, index) => (
        <MarkerF
          key={`${point.latitude}-${point.longitude}-${index}`}
          position={{
            lat: toFloat(point.latitude),
            lng: toFloat(point.longitude),
          }}
          label={{
            text: getMarkerLabel(index),
            color: '#f3f4f6',
            fontSize: '16px',
            fontWeight: '700',
          }}
          title={point.address}
        />
      ))}
    </GoogleMap>
  );
}

export default NPMapViewDirections;
